import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Switch,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@tonic/shared/components/ui';
import { Save, Utensils, Thermometer, Clock } from 'lucide-react';

export function RefillSettingsPage() {
  const [generalSettings, setGeneralSettings] = React.useState({
    enableAutoRefills: true,
    enableNotifications: true,
    defaultRefillPeriod: '30',
    minimumContainerCondition: 'good',
    enableSubscriptions: true
  });

  const [meatballSettings, setMeatballSettings] = React.useState({
    enableMeatballRefills: true,
    meatballTemperature: '60',
    maxStorageTime: '4',
    enableLingonberrySauce: true,
    enableWeeklySubscription: true,
    restaurantDiscount: '10'
  });

  const [stationSettings, setStationSettings] = React.useState({
    operatingHours: '9-21',
    maintenanceInterval: '7',
    enableAutoMaintenance: true,
    enableCapacityAlerts: true
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Refill Settings</h1>
          <p className="text-sm text-muted-foreground">
            Configure refill program settings and preferences
          </p>
        </div>
        <Button>
          <Save className="w-4 h-4 mr-2" />
          Save Changes
        </Button>
      </div>

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="meatballs">Meatball Program</TabsTrigger>
          <TabsTrigger value="stations">Stations</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>General Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable Auto Refills</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically process refills based on schedule
                  </p>
                </div>
                <Switch
                  checked={generalSettings.enableAutoRefills}
                  onCheckedChange={(checked) =>
                    setGeneralSettings(prev => ({ ...prev, enableAutoRefills: checked }))
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Send notifications for refill events
                  </p>
                </div>
                <Switch
                  checked={generalSettings.enableNotifications}
                  onCheckedChange={(checked) =>
                    setGeneralSettings(prev => ({ ...prev, enableNotifications: checked }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Default Refill Period (days)</Label>
                <Input
                  type="number"
                  value={generalSettings.defaultRefillPeriod}
                  onChange={(e) =>
                    setGeneralSettings(prev => ({ ...prev, defaultRefillPeriod: e.target.value }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Minimum Container Condition</Label>
                <Select
                  value={generalSettings.minimumContainerCondition}
                  onValueChange={(value) =>
                    setGeneralSettings(prev => ({ ...prev, minimumContainerCondition: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excellent">Excellent</SelectItem>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="fair">Fair</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="meatballs" className="space-y-4 mt-4">
          <Card className="border-[#0058AB]">
            <CardHeader className="flex flex-row items-center space-y-0">
              <Utensils className="w-6 h-6 mr-2 text-[#0058AB]" />
              <CardTitle>HUVUDROLL Meatball Program Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable Meatball Refills</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow meatball refill service
                  </p>
                </div>
                <Switch
                  checked={meatballSettings.enableMeatballRefills}
                  onCheckedChange={(checked) =>
                    setMeatballSettings(prev => ({ ...prev, enableMeatballRefills: checked }))
                  }
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Thermometer className="w-4 h-4 text-[#0058AB]" />
                  <Label>Meatball Temperature (°C)</Label>
                </div>
                <Input
                  type="number"
                  value={meatballSettings.meatballTemperature}
                  onChange={(e) =>
                    setMeatballSettings(prev => ({ ...prev, meatballTemperature: e.target.value }))
                  }
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#0058AB]" />
                  <Label>Maximum Storage Time (hours)</Label>
                </div>
                <Input
                  type="number"
                  value={meatballSettings.maxStorageTime}
                  onChange={(e) =>
                    setMeatballSettings(prev => ({ ...prev, maxStorageTime: e.target.value }))
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Include Lingonberry Sauce</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically include sauce with refills
                  </p>
                </div>
                <Switch
                  checked={meatballSettings.enableLingonberrySauce}
                  onCheckedChange={(checked) =>
                    setMeatballSettings(prev => ({ ...prev, enableLingonberrySauce: checked }))
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable Weekly Subscription</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow weekly meatball subscription plan
                  </p>
                </div>
                <Switch
                  checked={meatballSettings.enableWeeklySubscription}
                  onCheckedChange={(checked) =>
                    setMeatballSettings(prev => ({ ...prev, enableWeeklySubscription: checked }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Restaurant Discount (%)</Label>
                <Input
                  type="number"
                  value={meatballSettings.restaurantDiscount}
                  onChange={(e) =>
                    setMeatballSettings(prev => ({ ...prev, restaurantDiscount: e.target.value }))
                  }
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stations" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Station Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Operating Hours</Label>
                <Input
                  value={stationSettings.operatingHours}
                  onChange={(e) =>
                    setStationSettings(prev => ({ ...prev, operatingHours: e.target.value }))
                  }
                  placeholder="e.g., 9-21"
                />
              </div>

              <div className="space-y-2">
                <Label>Maintenance Interval (days)</Label>
                <Input
                  type="number"
                  value={stationSettings.maintenanceInterval}
                  onChange={(e) =>
                    setStationSettings(prev => ({ ...prev, maintenanceInterval: e.target.value }))
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable Auto Maintenance</Label>
                  <p className="text-sm text-muted-foreground">
                    Schedule maintenance automatically
                  </p>
                </div>
                <Switch
                  checked={stationSettings.enableAutoMaintenance}
                  onCheckedChange={(checked) =>
                    setStationSettings(prev => ({ ...prev, enableAutoMaintenance: checked }))
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable Capacity Alerts</Label>
                  <p className="text-sm text-muted-foreground">
                    Alert when station capacity is low
                  </p>
                </div>
                <Switch
                  checked={stationSettings.enableCapacityAlerts}
                  onCheckedChange={(checked) =>
                    setStationSettings(prev => ({ ...prev, enableCapacityAlerts: checked }))
                  }
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Notification Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Notification settings coming soon...</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
} 
